import React from 'react';
import { Observe } from 'rewire-core';
import { DateRange } from 'rewire-common';
import { Theme, CSSTheme } from 'rewire-ui';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import FolderOpenIcon from '@material-ui/icons/FolderOpenTwoTone';
import RefreshIcon from '@material-ui/icons/Refresh';
import { baseUrl } from '../graphql/GraphQLClient';
import { IReport } from '../DataTypes';
import { reportRouteManager } from '../main-navigation/RouteManager';
import { StyleType } from '../utility/styles';
import reportModel from './ReportModel';
import ReportToolbar from './ReportToolbar';
// import { embedDashboard } from '@superset-ui/embedded-sdk';
// import DataQueries from '../graphql/Queries';

// function dashboard() {
//   const doc = document.getElementById('dashboard-wrapper') as any;
//   console.log(doc);
//   embedDashboard({
//     id: '407ab0fc-805e-400e-bfbc-5dc67f32229f', // given by the Superset embedding UI
//     supersetDomain: 'https://analytics.worksight.services',
//     mountPoint: doc, // any html element that can contain an iframe
//     fetchGuestToken: DataQueries.getDashboardGuestToken,
//     dashboardUiConfig: {
//       // dashboard UI config: hideTitle, hideTab, hideChartControls, filters.visible, filters.expanded (optional), urlParams (optional)
//       hideTitle: true,
//       filters: {
//         expanded: true,
//       },
//       urlParams: {
//         foo: 'value1',
//         bar: 'value2',
//         // ...
//       },
//     },
//   });
// }

const styles = (theme: Theme) =>
  ({
    root: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '0px',
      overflow: 'hidden',
      position: 'relative',
    },
    reportContainer: {
      display: 'flex',
      flex: '1',
    },
    reportContent: {
      flexGrow: 14,
      position: 'relative',
      backgroundColor: '#FAFAFA',
    },
    reportList: {
      flexGrow: 2,
      padding: '5px',
    },
    buttonDiv: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      flex: '0 0 auto',
      flexWrap: 'wrap',
      margin: '0px 0px 5px 0px !important',
    },
    container: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-start',
      flex: '1 1 auto',
      flexWrap: 'wrap',
    },
    pagingAndControlsContainer: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
    },
    loading: {
      backgroundColor: '#00000030',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      width: '100%',
      height: '100%',
    },
    runReportWrapper: {
      backgroundColor: theme.palette.neutralLight.main,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      width: '100%',
      height: '100%',
    },
    instructions: {
      display: 'flex',
      backgroundColor: theme.palette.neutralLight.main,
      color: '#727272',
      fontSize: '24px',
      fontStyle: 'italic',
      marginTop: '10px',
      paddingLeft: '30px',
      paddingRight: '30px',
    },
    downloadInstructions: {
      display: 'flex',
      color: theme.palette.warning.contrastText,
      backgroundColor: theme.palette.warning.main,
      fontSize: '24px',
      fontStyle: 'italic',
      marginBottom: '15px',
      borderRadius: '10px',
      padding: '10px',
      width: '50%',
      cursor: 'default',
    },
    runReportButton: {
      display: 'flex',
      fontSize: '16px',
      color: theme.palette.warning.contrastText,
      backgroundColor: theme.palette.warning.main,
      '&:hover': {
        backgroundColor: theme.palette.warning.light,
      },
    },
    folder: {
      fontStyle: 'italic',
    },
    folderIcon: {
      minWidth: 24,
    },
    folderSize: {
      width: 18,
      color: 'orange',
    },
  }) as CSSTheme;

interface IReportFrame {
  src: string;
}

const Frame = (props: IReportFrame) => {
  return (
    <iframe title="WorkSight Flow Report" name="previewFrame" src={props.src} height="100%" width="100%" style={{ border: '0' }} />
  );
};

function mapReports(reports: IReport[]) {
  return reports
    .sort((r1, r2) => r1.path.localeCompare(r2.path))
    .reduce((prev, current) => {
      let x: any = prev;
      for (const segment of current.path.split('/')) {
        if (segment.length > 0) {
          x = x[segment] || (x[segment] = {});
        }
      }
      (x.reports || (x.reports = [])).push(current);
      return prev;
    }, {});
}

function navigateTo(report: IReport) {
  const selectedReportId = report?.id;
  if (!selectedReportId) return;

  let effective = reportRouteManager.effective;
  if (effective) {
    const maxDuration = reportModel.dateDurationDays(report);
    if (effective.length > maxDuration) {
      effective = new DateRange(effective.start, effective.start.add(maxDuration - 1));
    }
  }
  reportModel.select(selectedReportId);
  reportRouteManager.navigateTo({ selectedReportId, effective });
}

const levelIndent = 12;
const ReportFolder = ({
  reportFolder,
  name,
  level,
  classes,
}: {
  reportFolder: { reports?: IReport[] } & Record<string, unknown>;
  name: string;
  level: number;
  classes?: StyleType<typeof useReportStyles>;
}) => {
  const reportClasses = useReportStyles({ classes });
  const reports =
    reportFolder.reports?.map((r) => (
      <Observe key={r.id}>
        {() => (
          <ListItem
            onClick={() => navigateTo(r)}
            disabled={reportModel.rendering}
            key={r.id}
            selected={(r as any).selected}
            style={{ paddingLeft: level * levelIndent + levelIndent + 12, paddingTop: 0, paddingBottom: 0 }}
            button
          >
            <ListItemText primary={r.name} />
          </ListItem>
        )}
      </Observe>
    )) || null;
  const reportFolders = Object.keys(reportFolder)
    .filter((v) => v !== 'reports')
    .map((key) => (
      <ReportFolder reportFolder={reportFolder[key] as any} key={key} name={key} level={level + 1} classes={classes} />
    ));

  return (
    <>
      {name ? (
        <ListItem selected={false} key={name} style={{ paddingLeft: level * levelIndent, paddingTop: 0, paddingBottom: 0 }}>
          <ListItemIcon className={reportClasses.folderIcon}>
            <FolderOpenIcon className={reportClasses.folderSize} />
          </ListItemIcon>
          <ListItemText classes={{ primary: reportClasses.folder }} primary={name} />
        </ListItem>
      ) : null}
      {reports}
      {reportFolders?.length ? reportFolders : null}
    </>
  );
};

const Reports = (props: any) => {
  return (
    <Observe>
      {() => (
        <List component="nav">
          <ReportFolder reportFolder={mapReports(reportModel.items)} name="" level={-1} classes={props.classes} />
        </List>
      )}
    </Observe>
  );
};

const useReportStyles = makeStyles(styles);

export default (props: any) => {
  const classes = useReportStyles(props);

  return (
    <div className={classes.root}>
      <ReportToolbar />
      <div className={classes.reportContainer}>
        <Paper elevation={1} className={classes.reportList}>
          <Reports />
        </Paper>
        <Observe>
          {() => {
            if (reportModel.shouldTriggerDownload) {
              const downloadLink = document.getElementById('download-link');
              if (downloadLink) {
                setTimeout(() => reportModel.triggerDownload(downloadLink), 0);
                return null;
              }
            }

            const renderedReportId = reportModel.renderedReportId;
            const reportName = `${reportModel.selectedItem?.name}${reportModel.reportExtension}`;
            const progressIndicator = reportModel.rendering ? (
              <div className={classes.loading}>
                <CircularProgress />
              </div>
            ) : null;
            const reportSource = renderedReportId && `${baseUrl}/reports/${renderedReportId}/${reportName || ''}`;

            const isDisplayingDownloadInstructions = displayDownloadInstructions;
            let reportFrame = !isDisplayingDownloadInstructions && reportSource ? <Frame src={reportSource} /> : null;
            const instructionsText =
              isDisplayingDownloadInstructions || progressIndicator || reportFrame ? null : reportModel.selectedItem &&
                reportName ? (
                <span>Properties have changed &mdash; click « Run Report » to view the report</span>
              ) : (
                <span>No report currently selected</span>
              );

            const instructions = instructionsText ? (
              <Typography className={classes.instructions}>{instructionsText}</Typography>
            ) : null;

            const runReportButton =
              instructions && reportModel.selectedItem && reportName ? (
                <Button
                  className={classes.runReportButton}
                  disabled={!reportModel.canRender()}
                  variant="contained"
                  onClick={reportModel.renderReport}
                >
                  <RefreshIcon className="add">{'add'}</RefreshIcon>
                  Run Report
                </Button>
              ) : null;

            if (reportFrame && reportModel.isReportDownloadable()) {
              reportFrame = null;

              const downloadMessage = `The report ${reportName} must be downloaded in order to view it. Please check your browser download directory. Note: you may need to give your browser permission to download files.`;
              displayDownloadInstructions = (
                <Typography onClick={hideDownloadMessage} className={classes.downloadInstructions}>
                  {downloadMessage}
                </Typography>
              );
              setTimeout(hideDownloadMessage, 5_000);

              downloadLink = (
                <a id="download-link" style={{ display: 'none' }} href={`${reportSource}`} download target="_">
                  Download
                </a>
              );
              setTimeout(() => (reportModel.shouldTriggerDownload = true), 100);
            }

            return (
              <div id="dashboard-wrapper" className={classes.reportContent}>
                {progressIndicator}
                {reportFrame}
                {(instructions || displayDownloadInstructions) && (
                  <div className={classes.runReportWrapper}>
                    {downloadLink}
                    {displayDownloadInstructions}
                    <div>{runReportButton}</div>
                    {instructions}
                  </div>
                )}
              </div>
            );
          }}
        </Observe>
      </div>
    </div>
  );
};

function hideDownloadMessage() {
  displayDownloadInstructions = null;
  downloadLink = null;
  reportModel.pulseRefresh();
}

let displayDownloadInstructions: any;
let downloadLink: any;
